import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import useSextforceLiveStreamAutoAnnouncementsMessages, {
    OnlyFansSextfoceLiveStreamAnnouncementMessage,
} from '../../../../../hooks/useSextforceLiveStreamAutoAnnouncementsMessages';
import { OnlyFansSubscriber } from '../../../../../hooks/useSubscriber';
import SextforceLiveStreamAutoAnnouncementsMessageForm from './SextforceLiveStreamAutoAnnouncementsMessageForm';

const SextforceLiveStreamAutoAnnouncementsMessageRow = ({
    subscriber,
    message,
    groupId,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
    isDragging,
    draggedOverIndex,
    index,
    currentDragType,
    currentDragGroupId,
    isGroupActive,
}: {
    subscriber: OnlyFansSubscriber;
    message: OnlyFansSextfoceLiveStreamAnnouncementMessage;
    groupId: string;
    onDragStart: (e: React.DragEvent, index: number, type: 'message', groupId: string) => void;
    onDragEnd: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent, index: number, type: 'message', groupId: string) => void;
    onDrop: (e: React.DragEvent, index: number, type: 'message', groupId: string) => void;
    isDragging: boolean;
    draggedOverIndex: number | null;
    index: number;
    currentDragType: 'group' | 'message' | null;
    currentDragGroupId: string | null;
    isGroupActive: boolean;
}) => {
    const theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const {
        refetch: refetchLiveStreamAutoAnnouncementsMessages,
        sextforceLiveStreamAutoAnnouncementMessageUpdate,
        isSextforceAutoAnnouncementMessageUpdateLoading,
        sextforceLiveStreamAutoAnnouncementMessageDelete,
        isSextforceAutoAnnouncementMessageDeleteLoading,
        sextforceLiveStreamAutoAnnouncementMessageActiveUpdate,
        isSextforceAutoAnnouncementMessageActiveUpdateLoading,
    } = useSextforceLiveStreamAutoAnnouncementsMessages();

    const [showEditForm, setShowEditForm] = useState<boolean>(false);

    // Memoize drag styles to prevent recalculation on every render
    const dragStyles = useMemo(() => {
        const baseStyles = {
            p: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            cursor: 'default',
            // Reduce opacity if group is inactive
            opacity: isGroupActive ? 1 : 0.5,
        };

        if (isDragging) {
            return {
                ...baseStyles,
                opacity: 0.5,
                transform: 'scale(0.95)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
            };
        }

        if (draggedOverIndex === index) {
            return {
                ...baseStyles,
                borderTop: `2px solid ${theme.palette.primary.main}`,
                backgroundColor: theme.palette.action.hover,
                transition: 'border-top 0.2s ease, background-color 0.2s ease, opacity 0.2s ease',
            };
        }

        return {
            ...baseStyles,
            transition: 'opacity 0.2s ease, transform 0.2s ease, border-top 0.2s ease, background-color 0.2s ease',
        };
    }, [isDragging, draggedOverIndex, index, theme, isGroupActive]);

    // Memoize drag handle styles
    const dragHandleStyles = useMemo(
        () => ({
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 1,
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
            mt: 0.5,
        }),
        [theme],
    );

    // Optimize drag event handlers with useCallback
    const handleDragStart = useCallback(
        (e: React.DragEvent) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = 'move';
            onDragStart(e, index, 'message', groupId);
        },
        [onDragStart, index, groupId],
    );

    const handleDragEnd = useCallback(
        (e: React.DragEvent) => {
            e.stopPropagation();
            onDragEnd(e);
        },
        [onDragEnd],
    );

    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            // Only allow drag over for messages within the same group
            if (currentDragType === 'message' && currentDragGroupId === groupId) {
                e.dataTransfer.dropEffect = 'move';
                onDragOver(e, index, 'message', groupId);
            } else {
                e.dataTransfer.dropEffect = 'none';
            }
        },
        [onDragOver, index, groupId, currentDragType, currentDragGroupId],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            // Only allow drop for messages within the same group
            if (currentDragType === 'message' && currentDragGroupId === groupId) {
                onDrop(e, index, 'message', groupId);
            }
        },
        [onDrop, index, groupId, currentDragType, currentDragGroupId],
    );

    const handleDelete = useCallback(
        (messageId: string) => {
            dialog
                .confirm({
                    message: 'Are you sure you want to delete this message?',
                    title: 'Delete Live Stream Auto Announcement Message',
                    ok: { text: 'Delete', color: 'error' },
                    cancel: { text: 'Cancel', color: 'primary' },
                })
                .then(() => {
                    sextforceLiveStreamAutoAnnouncementMessageDelete(messageId)
                        .then(() => {
                            enqueueSnackbar('Live Stream Auto Announcement Message deleted successfully', {
                                variant: 'success',
                                autoHideDuration: 3000,
                            });
                            refetchLiveStreamAutoAnnouncementsMessages();
                            setShowEditForm(false);
                        })
                        .catch(error => {
                            enqueueSnackbar('Failed to delete Live Stream Auto Announcement Message', {
                                variant: 'error',
                                autoHideDuration: 3000,
                            });
                        });
                })
                .catch(() => {
                    // User cancelled the delete action
                });
        },
        [dialog, sextforceLiveStreamAutoAnnouncementMessageDelete, enqueueSnackbar, refetchLiveStreamAutoAnnouncementsMessages],
    );

    const handleActiveToggle = useCallback(() => {
        sextforceLiveStreamAutoAnnouncementMessageActiveUpdate(message._id!, !message.active)
            .then(() => {
                enqueueSnackbar(`Live Stream Auto Announcement Message ${message.active ? 'deactivated' : 'activated'} successfully`, {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
                refetchLiveStreamAutoAnnouncementsMessages();
            })
            .catch(error => {
                enqueueSnackbar('Failed to update Live Stream Auto Announcement Message status', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            });
    }, [
        message._id,
        message.active,
        sextforceLiveStreamAutoAnnouncementMessageActiveUpdate,
        enqueueSnackbar,
        refetchLiveStreamAutoAnnouncementsMessages,
    ]);

    // Memoize message content rendering
    const messageContent = useMemo(
        () =>
            message.message.split('\n').map((line, index) => (
                <span key={index}>
                    {line}
                    <br />
                </span>
            )),
        [message.message],
    );

    return (
        <Box sx={dragStyles} onDragOver={handleDragOver} onDrop={handleDrop}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={dragHandleStyles} draggable onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    <DragHandleIcon fontSize="small" sx={{ pointerEvents: 'none' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                        {message.name}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {messageContent}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small" onClick={handleActiveToggle} disabled={isSextforceAutoAnnouncementMessageActiveUpdateLoading}>
                        <PowerSettingsNewIcon fontSize="small" color={message.active ? 'success' : 'disabled'} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setShowEditForm(true)}
                        disabled={isSextforceAutoAnnouncementMessageUpdateLoading}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDelete(message._id!)}
                        disabled={isSextforceAutoAnnouncementMessageDeleteLoading}
                    >
                        <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                </Box>
            </Box>

            {showEditForm && (
                <SextforceLiveStreamAutoAnnouncementsMessageForm
                    subscriber={subscriber}
                    groupId={groupId}
                    initialValue={message}
                    onSubmit={updatedMessage => {
                        sextforceLiveStreamAutoAnnouncementMessageUpdate(message._id!, updatedMessage)
                            .then(updatedMessage => {
                                if (updatedMessage && updatedMessage._id) {
                                    enqueueSnackbar('Live Stream Auto Announcement Message updated successfully', {
                                        variant: 'success',
                                        autoHideDuration: 3000,
                                    });
                                    refetchLiveStreamAutoAnnouncementsMessages();
                                    setShowEditForm(false);
                                } else {
                                    enqueueSnackbar('Failed to update Live Stream Auto Announcement Message', {
                                        variant: 'error',
                                        autoHideDuration: 3000,
                                    });
                                }
                            })
                            .catch(error => {
                                enqueueSnackbar('Failed to update Live Stream Auto Announcement Message', {
                                    variant: 'error',
                                    autoHideDuration: 3000,
                                });
                            });
                    }}
                    isLoading={isSextforceAutoAnnouncementMessageUpdateLoading}
                    onCancel={() => {
                        setShowEditForm(false);
                    }}
                    open={showEditForm}
                />
            )}
        </Box>
    );
};

export default SextforceLiveStreamAutoAnnouncementsMessageRow;
